/**
 * RBAC-PHI Service
 * Role-Based Access Control service for Protected Health Information
 *
 * This service enforces PHI access controls based on user roles and
 * PHI classification levels. All authorization decisions are audited
 * for HIPAA compliance.
 */

import {
  PHIAccessOperation,
  PHIAccessRequest,
  PHIAccessDecision,
  PHIAccessPolicy,
  PHI_ACCESS_MATRIX,
  EmergencyAccess,
  DeIdentificationLevel,
  ROLE_DEIDENTIFICATION_MAP,
  TimeBasedAccess,
  ROLE_SESSION_TIMEOUTS,
} from '../types/rbac-phi.types';
import { UserRole } from '../types/user.types';
import { PHIClassification } from '../types/phi.types';
import { logPHIAccess } from './audit-log.service';
import {
  PHIAccessAction,
  AuditResourceType,
} from '../types/audit.types';

/**
 * RBAC-PHI Service Error
 */
export class RBACError extends Error {
  constructor(
    message: string,
    public code: string,
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'RBACError';
  }
}

/**
 * Emergency access store
 * In production, this would be stored in a database
 */
class EmergencyAccessStore {
  private static emergencyAccesses: Map<string, EmergencyAccess> = new Map();

  static grant(access: EmergencyAccess): void {
    const key = `${access.userId}-${access.patientId}`;
    this.emergencyAccesses.set(key, access);
  }

  static revoke(userId: string, patientId: string): void {
    const key = `${userId}-${patientId}`;
    this.emergencyAccesses.delete(key);
  }

  static check(userId: string, patientId: string): EmergencyAccess | null {
    const key = `${userId}-${patientId}`;
    const access = this.emergencyAccesses.get(key);

    if (!access) return null;

    // Check if emergency access is still valid
    const now = new Date();
    const endTime = new Date(access.startTime.getTime() + access.duration * 60000);

    if (now > endTime) {
      this.emergencyAccesses.delete(key);
      return null;
    }

    return access;
  }

  static getAll(): EmergencyAccess[] {
    return Array.from(this.emergencyAccesses.values());
  }
}

/**
 * RBAC-PHI Service
 * Singleton service for managing PHI access control
 */
class RBACPHIService {
  /**
   * Check if a user can perform an operation on PHI
   *
   * @param request - The PHI access request
   * @returns Access decision with audit information
   */
  async authorize(request: PHIAccessRequest): Promise<PHIAccessDecision> {
    try {
      // Check for emergency access first
      const emergencyAccess = this.checkEmergencyAccess(
        request.userId,
        request.resourceId
      );

      let effectiveRole = request.userRole;
      let isEmergency = false;

      if (emergencyAccess) {
        effectiveRole = emergencyAccess.elevatedRole;
        isEmergency = true;
      }

      // Get the access policy for the user's role
      const policy = PHI_ACCESS_MATRIX[effectiveRole];

      if (!policy) {
        await this.auditDeniedAccess(request, 'Invalid role');
        return {
          allowed: false,
          reason: 'Invalid user role',
          auditRequired: true,
        };
      }

      // Check if the classification is allowed for this role
      const classificationAllowed = policy.allowedClassifications.includes(
        request.classification
      );

      if (!classificationAllowed) {
        await this.auditDeniedAccess(
          request,
          `Role ${effectiveRole} cannot access ${request.classification} data`
        );
        return {
          allowed: false,
          reason: `Your role does not have access to ${request.classification} data`,
          auditRequired: true,
        };
      }

      // Check if the operation is allowed for this role
      const operationAllowed = policy.allowedOperations.includes(
        request.operation
      );

      if (!operationAllowed) {
        await this.auditDeniedAccess(
          request,
          `Role ${effectiveRole} cannot perform ${request.operation} operation`
        );
        return {
          allowed: false,
          reason: `Your role cannot perform ${request.operation} operations`,
          auditRequired: true,
        };
      }

      // Check if justification is required
      if (policy.requiresJustification && !request.justification) {
        await this.auditDeniedAccess(
          request,
          'Justification required but not provided'
        );
        return {
          allowed: false,
          reason: 'Access justification is required for your role',
          requiresJustification: true,
          auditRequired: true,
        };
      }

      // Check time-based restrictions
      const timeRestriction = this.checkTimeRestrictions(effectiveRole);
      if (timeRestriction) {
        await this.auditDeniedAccess(request, timeRestriction);
        return {
          allowed: false,
          reason: timeRestriction,
          auditRequired: true,
        };
      }

      // Access is allowed - audit the successful access
      await this.auditAllowedAccess(request, isEmergency);

      return {
        allowed: true,
        auditRequired: policy.requiresAudit,
        restrictions: this.getAccessRestrictions(effectiveRole, request.classification),
      };

    } catch (error) {
      console.error('RBAC authorization error:', error);

      // On error, deny access for security
      await this.auditDeniedAccess(request, 'Authorization error');

      return {
        allowed: false,
        reason: 'Authorization system error',
        auditRequired: true,
      };
    }
  }

  /**
   * Check if a user has access to a specific PHI classification
   */
  canAccessClassification(
    userRole: UserRole,
    classification: PHIClassification
  ): boolean {
    const policy = PHI_ACCESS_MATRIX[userRole];
    if (!policy) return false;

    return policy.allowedClassifications.includes(classification);
  }

  /**
   * Check if a user can perform a specific operation
   */
  canPerformOperation(
    userRole: UserRole,
    operation: PHIAccessOperation
  ): boolean {
    const policy = PHI_ACCESS_MATRIX[userRole];
    if (!policy) return false;

    return policy.allowedOperations.includes(operation);
  }

  /**
   * Get the de-identification level required for a role
   */
  getDeIdentificationLevel(userRole: UserRole): DeIdentificationLevel {
    return ROLE_DEIDENTIFICATION_MAP[userRole] || DeIdentificationLevel.FULL;
  }

  /**
   * Get session timeout for a role
   */
  getSessionTimeout(userRole: UserRole): number {
    return ROLE_SESSION_TIMEOUTS[userRole] || 15; // Default 15 minutes
  }

  /**
   * Grant emergency access to PHI
   */
  async grantEmergencyAccess(
    access: EmergencyAccess
  ): Promise<void> {
    // Validate the emergency access request
    if (access.duration > 240) { // Max 4 hours
      throw new RBACError(
        'Emergency access cannot exceed 4 hours',
        'INVALID_DURATION'
      );
    }

    // Store the emergency access
    EmergencyAccessStore.grant(access);

    // Audit the emergency access grant
    await logPHIAccess({
      userId: access.approvedBy || 'system',
      action: PHIAccessAction.CREATE,
      resourceType: AuditResourceType.AUDIT_LOG,
      resourceId: `emergency-${access.userId}-${access.patientId}`,
      classification: PHIClassification.DIRECT_IDENTIFIER,
      success: true,
      metadata: {
        type: 'emergency_access_granted',
        userId: access.userId,
        patientId: access.patientId,
        duration: access.duration,
        reason: access.reason,
      },
    });
  }

  /**
   * Revoke emergency access
   */
  async revokeEmergencyAccess(
    userId: string,
    patientId: string,
    revokedBy: string
  ): Promise<void> {
    EmergencyAccessStore.revoke(userId, patientId);

    // Audit the revocation
    await logPHIAccess({
      userId: revokedBy,
      action: PHIAccessAction.DELETE,
      resourceType: AuditResourceType.AUDIT_LOG,
      resourceId: `emergency-${userId}-${patientId}`,
      classification: PHIClassification.DIRECT_IDENTIFIER,
      success: true,
      metadata: {
        type: 'emergency_access_revoked',
        userId,
        patientId,
      },
    });
  }

  /**
   * Check if a user has active emergency access
   */
  private checkEmergencyAccess(
    userId: string,
    resourceId: string
  ): EmergencyAccess | null {
    // For patient resources, check emergency access
    if (resourceId.startsWith('patient-')) {
      const patientId = resourceId.replace('patient-', '');
      return EmergencyAccessStore.check(userId, patientId);
    }

    return null;
  }

  /**
   * Check time-based access restrictions
   */
  private checkTimeRestrictions(userRole: UserRole): string | null {
    const now = new Date();
    const hour = now.getHours();
    const day = now.getDay();

    // Example: Receptionist can only access during business hours
    if (userRole === 'receptionist') {
      if (hour < 8 || hour >= 18) {
        return 'Access restricted outside business hours (8 AM - 6 PM)';
      }
      if (day === 0 || day === 6) {
        return 'Access restricted on weekends';
      }
    }

    return null;
  }

  /**
   * Get access restrictions for a role and classification
   */
  private getAccessRestrictions(
    userRole: UserRole,
    classification: PHIClassification
  ): string[] {
    const restrictions: string[] = [];

    // Add de-identification requirement
    const deIdLevel = this.getDeIdentificationLevel(userRole);
    if (deIdLevel !== DeIdentificationLevel.NONE) {
      restrictions.push(`Data must be ${deIdLevel} de-identified`);
    }

    // Add session timeout
    const timeout = this.getSessionTimeout(userRole);
    restrictions.push(`Session timeout: ${timeout} minutes`);

    // Add classification-specific restrictions
    if (classification === PHIClassification.FINANCIAL_INFO) {
      restrictions.push('Financial data access is logged for compliance');
    }

    if (classification === PHIClassification.DIRECT_IDENTIFIER) {
      restrictions.push('Direct identifiers require extra audit scrutiny');
    }

    return restrictions;
  }

  /**
   * Audit allowed PHI access
   */
  private async auditAllowedAccess(
    request: PHIAccessRequest,
    isEmergency: boolean = false
  ): Promise<void> {
    await logPHIAccess({
      userId: request.userId,
      action: this.mapOperationToAction(request.operation),
      resourceType: AuditResourceType.PATIENT_PROFILE,
      resourceId: request.resourceId,
      classification: request.classification,
      success: true,
      metadata: {
        purpose: request.purpose,
        justification: request.justification,
        isEmergency,
      },
    });
  }

  /**
   * Audit denied PHI access
   */
  private async auditDeniedAccess(
    request: PHIAccessRequest,
    reason: string
  ): Promise<void> {
    await logPHIAccess({
      userId: request.userId,
      action: this.mapOperationToAction(request.operation),
      resourceType: AuditResourceType.PATIENT_PROFILE,
      resourceId: request.resourceId,
      classification: request.classification,
      success: false,
      errorMessage: reason,
      metadata: {
        purpose: request.purpose,
        justification: request.justification,
      },
    });
  }

  /**
   * Map PHI operation to audit action
   */
  private mapOperationToAction(operation: PHIAccessOperation): PHIAccessAction {
    const mapping: Record<PHIAccessOperation, PHIAccessAction> = {
      [PHIAccessOperation.VIEW]: PHIAccessAction.VIEW,
      [PHIAccessOperation.CREATE]: PHIAccessAction.CREATE,
      [PHIAccessOperation.UPDATE]: PHIAccessAction.UPDATE,
      [PHIAccessOperation.DELETE]: PHIAccessAction.DELETE,
      [PHIAccessOperation.ENCRYPT]: PHIAccessAction.ENCRYPT,
      [PHIAccessOperation.DECRYPT]: PHIAccessAction.DECRYPT,
      [PHIAccessOperation.EXPORT]: PHIAccessAction.EXPORT,
      [PHIAccessOperation.SHARE]: PHIAccessAction.SHARE,
    };

    return mapping[operation] || PHIAccessAction.VIEW;
  }

  /**
   * Get all active emergency accesses
   */
  getActiveEmergencyAccesses(): EmergencyAccess[] {
    return EmergencyAccessStore.getAll();
  }

  /**
   * Check if role requires justification for access
   */
  requiresJustification(
    userRole: UserRole,
    classification: PHIClassification
  ): boolean {
    const policy = PHI_ACCESS_MATRIX[userRole];
    if (!policy) return true; // Require by default for invalid roles

    // Extra sensitive data always requires justification for non-admin
    if (userRole !== 'admin' &&
        classification === PHIClassification.DIRECT_IDENTIFIER) {
      return true;
    }

    return policy.requiresJustification || false;
  }

  /**
   * Get maximum retention days for a role
   */
  getMaxRetentionDays(userRole: UserRole): number {
    const policy = PHI_ACCESS_MATRIX[userRole];
    return policy?.maxRetentionDays || 30; // Default 30 days
  }
}

// Export singleton instance
export const rbacPHIService = new RBACPHIService();

// Export convenience functions
export const authorizePHIAccess = (request: PHIAccessRequest) =>
  rbacPHIService.authorize(request);

export const canAccessPHIClassification = (
  userRole: UserRole,
  classification: PHIClassification
) => rbacPHIService.canAccessClassification(userRole, classification);

export const canPerformPHIOperation = (
  userRole: UserRole,
  operation: PHIAccessOperation
) => rbacPHIService.canPerformOperation(userRole, operation);

export const getPHIDeIdentificationLevel = (userRole: UserRole) =>
  rbacPHIService.getDeIdentificationLevel(userRole);

export const getRoleSessionTimeout = (userRole: UserRole) =>
  rbacPHIService.getSessionTimeout(userRole);

export const grantEmergencyPHIAccess = (access: EmergencyAccess) =>
  rbacPHIService.grantEmergencyAccess(access);

export const revokeEmergencyPHIAccess = (
  userId: string,
  patientId: string,
  revokedBy: string
) => rbacPHIService.revokeEmergencyAccess(userId, patientId, revokedBy);